import { AlertCircle, Check } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { Mono } from "@/components/Badges";
const DOT_TONE = {
  done: "border-emerald-600 bg-emerald-600 text-white",
  current: "border-emerald-600 bg-background text-emerald-700 dark:text-emerald-300",
  outstanding: "border-amber-500 bg-background text-amber-600 dark:text-amber-400",
  pending: "border-border bg-background text-transparent",
};

/** The PO journey as a tick list. Tap the current step when onStageClick is given. */
export default function StageTracker({ steps = [], testId = "stage-tracker", onStageClick, activeStage }) {
  return (
    <ol
      className={`relative space-y-0 ${steps.length > 10 ? "max-h-[60vh] overflow-y-auto pr-1" : ""}`}
      data-testid={testId}
    >
      {steps.map((step, i) => {
        const tappable = !!onStageClick && (step.state === "current" || step.state === "outstanding");
        const Row = tappable ? "button" : "div";
        return (
          <li
            key={step.stage}
            className="relative flex gap-3 pb-4 last:pb-0"
            data-testid={`${testId}-step-${step.stage}`}
          >
            {i < steps.length - 1 && (
              <span
                className={`absolute left-[11px] top-6 h-full w-px ${
                  step.state === "done" ? "bg-emerald-600/60" : "bg-border"
                }`}
              />
            )}
            <span
              className={`relative z-10 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 ${DOT_TONE[step.state]}`}
              data-testid={`${testId}-dot-${step.stage}-${step.state}`}
            >
              {step.state === "done" && <Check className="h-3 w-3" strokeWidth={3} />}
              {step.state === "outstanding" && <AlertCircle className="h-3.5 w-3.5" />}
              {step.state === "current" && <span className="h-2 w-2 rounded-full bg-emerald-600" />}
            </span>
            <Row
              {...(tappable
                ? {
                    type: "button",
                    onClick: () => onStageClick(step),
                    className: "min-w-0 pt-0.5 text-left",
                    "data-testid": `${testId}-tap-${step.stage}`,
                  }
                : { className: "min-w-0 pt-0.5" })}
            >
              <div className="flex flex-wrap items-baseline gap-2">
                <span
                  className={`text-sm ${
                    step.state === "current"
                      ? "font-semibold text-foreground"
                      : step.state === "outstanding"
                      ? "font-medium text-amber-700 dark:text-amber-300"
                      : step.state === "done"
                      ? "text-foreground/70"
                      : "text-muted-foreground"
                  } ${tappable ? "underline decoration-dotted underline-offset-4" : ""}`}
                >
                  {step.label}
                </span>
                {step.state === "outstanding" && (
                  <span className="text-xs text-amber-700 dark:text-amber-300">still outstanding</span>
                )}
                {step.date && (
                  <Mono className="text-xs text-muted-foreground">
                    {fmtDate(step.date)}
                    {step.days || step.days === 0 ? ` · ${step.days}d` : ""}
                  </Mono>
                )}
              </div>
              {(step.state === "current" || step.state === "outstanding") && (
                <p className="mt-1 text-xs text-foreground/80">
                  {step.action}
                  {tappable && <span className="ml-1 font-medium text-primary">Tap to record</span>}
                </p>
              )}
            </Row>
          </li>
        );
      })}
    </ol>
  );
}
